import {
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Animated, Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import type { DrawerLayoutMethods } from 'react-native-gesture-handler/ReanimatedDrawerLayout';
import DrawerLayout from 'react-native-gesture-handler/ReanimatedDrawerLayout';
import { useLayoutChrome } from '../../contexts/LayoutChromeContext';
import { useTranslation } from '../../contexts/TranslationContext';
import { NotificationFeedContent } from '../../features/notifications/components/NotificationFeedSheet';
import { useResponsive } from '../../hooks/useResponsive';
import { secureStorage } from '../../lib/secureStorage';
import { colors } from '../../theme/colors';
import { tokens } from '../../theme/tokens';
import { Box } from '../primitives/Box';
import { MobileTabBar } from './MobileTabBar';

const SIDEBAR_EXPANDED_KEY = 'sidebar_expanded';
const SIDEBAR_WIDTH_EXPANDED = 264;
// Slim icon rail; sized so SidebarModern's fixed-position icons read centered.
const SIDEBAR_WIDTH_COLLAPSED = 56;
// Notification drawer is a touch wider than the burger sidebar so timestamps
// + body copy ("Mateo Aguilar moved from Prospect to Contacted") have room to
// breathe without forcing aggressive truncation. Tuned narrower than 1.2x —
// at 1.2 the panel ate ~80% of an iPhone viewport and looked oversized.
const NOTIFICATION_DRAWER_WIDTH = 300;
const MOBILE_HEADER_HEIGHT = 64;
const MOBILE_MENU_BUTTON_SIZE = 40;
const MAX_CONTENT_WIDTH = 1560;

interface DashboardLayoutProps {
  readonly sidebar: ReactNode;
  readonly header: ReactNode;
  readonly children: ReactNode;
  readonly aside?: ReactNode;
  readonly scrollable?: boolean;
}

interface SidebarLayoutProps {
  expanded: boolean;
  onToggle: () => void;
  onNavigate?: () => void;
}

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

// Flat dark-blue canvas. The previous "atmosphere" (teal/purple gradient + two
// pulsing glow orbs) was a legacy treatment; the design uses a single solid
// background behind every section.
//
// One tone for the whole shell on every platform: chrome and content share it,
// so nothing here needs to vary by layout.
function Atmosphere() {
  return (
    <View
      pointerEvents="none"
      style={[StyleSheet.absoluteFill, { backgroundColor: colors.background.primary }]}
    />
  );
}

interface DesktopLayoutProps {
  sidebarWithProps: ReactNode;
  header: ReactNode;
  mainContent: ReactNode;
  aside?: ReactNode;
  sidebarWidthAnim: Animated.Value;
  scrollable: boolean;
}

function DesktopLayout({
  sidebarWithProps,
  header,
  mainContent,
  aside,
  sidebarWidthAnim,
  scrollable,
}: Readonly<DesktopLayoutProps>) {
  const { edgeToEdge } = useLayoutChrome();
  const mainAreaContent = scrollable ? (
    <ScrollView
      style={styles.scrollFlex}
      showsVerticalScrollIndicator={false}
      {...(Platform.OS === 'web' ? ({ role: 'main' } as any) : {})}
    >
      <View style={edgeToEdge ? styles.scrollFlex : styles.desktopContentContainer}>
        {mainContent}
      </View>
    </ScrollView>
  ) : (
    <View
      style={[styles.scrollFlex, !edgeToEdge && styles.desktopContentPadding]}
      {...(Platform.OS === 'web' ? ({ role: 'main' } as any) : {})}
    >
      {mainContent}
    </View>
  );

  return (
    <View style={styles.desktopRoot}>
      <Atmosphere />

      <Animated.View
        style={[styles.desktopSidebarContainer, { width: sidebarWidthAnim }]}
        {...(Platform.OS === 'web' ? { role: 'navigation' } : {})}
      >
        {sidebarWithProps}
      </Animated.View>

      <View style={styles.desktopMainArea}>
        <View style={styles.desktopHeader} {...(Platform.OS === 'web' ? { role: 'banner' } : {})}>
          {header}
        </View>

        <View style={styles.desktopContentRow}>
          <View style={styles.desktopPanel}>{mainAreaContent}</View>

          {aside && (
            <View
              style={styles.desktopAside}
              {...(Platform.OS === 'web' ? { role: 'complementary' } : {})}
            >
              <ScrollView
                style={styles.asideScroll}
                contentContainerStyle={styles.asideScrollContent}
                showsVerticalScrollIndicator={false}
              >
                {aside}
              </ScrollView>
            </View>
          )}
        </View>
      </View>
    </View>
  );
}

interface NativeMobileLayoutProps {
  header: ReactNode;
  mainContent: ReactNode;
  aside?: ReactNode;
  getSidebarWithProps: (
    handler: () => void,
    expanded: boolean,
    onNavigate?: () => void,
  ) => ReactNode;
  t: (key: string) => string;
  scrollable: boolean;
}

function NativeMobileLayout({
  header,
  mainContent,
  aside,
  getSidebarWithProps,
  scrollable,
}: Readonly<NativeMobileLayoutProps>) {
  const drawerRef = useRef<DrawerLayoutMethods>(null);
  const {
    mobileTabBarHidden,
    edgeToEdge,
    registerMobileDrawerOpener,
    registerNotificationFeedOpener,
  } = useLayoutChrome();

  // Notification drawer uses an in-tree animated overlay anchored to the
  // right of mobileRoot rather than a nested ReanimatedDrawerLayout. Two
  // nested DrawerLayouts in this library don't reliably honor
  // `drawerPosition` — the outer drawer's slot collapses onto the inner
  // (left) drawer's frame and content renders on the wrong side. The
  // overlay below mirrors the WebMobileLayout pattern, which is known to
  // work, and keeps right-anchored positioning predictable.
  const [notificationOpen, setNotificationOpen] = useState(false);
  const notificationAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    registerMobileDrawerOpener(() => drawerRef.current?.openDrawer());
    return () => registerMobileDrawerOpener(null);
  }, [registerMobileDrawerOpener]);

  useEffect(() => {
    registerNotificationFeedOpener(() => setNotificationOpen(true));
    return () => registerNotificationFeedOpener(null);
  }, [registerNotificationFeedOpener]);

  useEffect(() => {
    Animated.spring(notificationAnim, {
      toValue: notificationOpen ? 1 : 0,
      damping: 22,
      mass: 0.9,
      stiffness: 260,
      useNativeDriver: true,
    }).start();
  }, [notificationAnim, notificationOpen]);

  const notificationTranslateX = notificationAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [NOTIFICATION_DRAWER_WIDTH + 24, 0],
  });
  const notificationBackdropOpacity = notificationAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1],
  });

  const renderDrawerContent = useCallback(
    () => (
      <View style={styles.drawerContent}>
        {getSidebarWithProps(
          () => drawerRef.current?.closeDrawer(),
          true,
          () => drawerRef.current?.closeDrawer(),
        )}
      </View>
    ),
    [getSidebarWithProps],
  );

  const mainAreaContent = scrollable ? (
    <ScrollView style={styles.scrollFlex} showsVerticalScrollIndicator={false}>
      <View style={edgeToEdge ? styles.scrollFlex : styles.mobileContentContainer}>
        {mainContent}
      </View>
    </ScrollView>
  ) : (
    <View style={[styles.scrollFlex, !edgeToEdge && styles.mobileContentPadding]}>
      {mainContent}
    </View>
  );

  return (
    <DrawerLayout
      ref={drawerRef}
      drawerWidth={SIDEBAR_WIDTH_EXPANDED}
      drawerBackgroundColor={colors.background.primary}
      overlayColor={colors.background.backdropTinted}
      renderNavigationView={renderDrawerContent}
    >
      <View style={styles.mobileRoot}>
        <Atmosphere />
        <View style={styles.mobileHeader}>{header}</View>
        {mainAreaContent}
        {aside && (
          <View style={styles.mobileAside}>
            <ScrollView showsVerticalScrollIndicator={false}>{aside}</ScrollView>
          </View>
        )}
        {!mobileTabBarHidden && <MobileTabBar />}

        <AnimatedPressable
          pointerEvents={notificationOpen ? 'auto' : 'none'}
          style={[styles.mobileBackdrop, { opacity: notificationBackdropOpacity }]}
          onPress={() => setNotificationOpen(false)}
        />
        <Animated.View
          pointerEvents={notificationOpen ? 'auto' : 'none'}
          style={[
            styles.notificationDrawer,
            { transform: [{ translateX: notificationTranslateX }] },
          ]}
        >
          <NotificationFeedContent onClose={() => setNotificationOpen(false)} withHeader />
        </Animated.View>
      </View>
    </DrawerLayout>
  );
}

interface WebMobileLayoutProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  header: ReactNode;
  mainContent: ReactNode;
  aside?: ReactNode;
  getSidebarWithProps: (
    handler: () => void,
    expanded: boolean,
    onNavigate?: () => void,
  ) => ReactNode;
  mobileMenuAnim: Animated.Value;
  mobileDrawerTranslateX: Animated.AnimatedInterpolation<number>;
  mobileBackdropOpacity: Animated.AnimatedInterpolation<number>;
  t: (key: string) => string;
  scrollable: boolean;
}

function WebMobileLayout({
  sidebarOpen,
  setSidebarOpen,
  header,
  mainContent,
  aside,
  getSidebarWithProps,
  mobileMenuAnim,
  mobileDrawerTranslateX,
  mobileBackdropOpacity,
  scrollable,
}: Readonly<WebMobileLayoutProps>) {
  const { mobileTabBarHidden, edgeToEdge, registerMobileDrawerOpener } = useLayoutChrome();

  useEffect(() => {
    registerMobileDrawerOpener(() => setSidebarOpen(true));
    return () => registerMobileDrawerOpener(null);
  }, [registerMobileDrawerOpener, setSidebarOpen]);
  const mainAreaContent = scrollable ? (
    <ScrollView
      style={styles.scrollFlex}
      showsVerticalScrollIndicator={false}
      {...({ role: 'main' } as any)}
    >
      <Box style={edgeToEdge ? styles.scrollFlex : styles.mobileContentContainer}>
        {mainContent}
      </Box>
    </ScrollView>
  ) : (
    <View
      style={[styles.scrollFlex, !edgeToEdge && styles.mobileContentPadding]}
      {...({ role: 'main' } as any)}
    >
      {mainContent}
    </View>
  );

  return (
    <View style={styles.mobileRoot}>
      <Atmosphere />

      <AnimatedPressable
        pointerEvents={sidebarOpen ? 'auto' : 'none'}
        style={[styles.mobileBackdrop, { opacity: mobileBackdropOpacity }]}
        onPress={() => setSidebarOpen(false)}
      />

      <Animated.View
        pointerEvents={sidebarOpen ? 'auto' : 'none'}
        style={[styles.mobileSidebar, { transform: [{ translateX: mobileDrawerTranslateX }] }]}
      >
        <View style={styles.mobileSidebarInner}>
          {getSidebarWithProps(
            () => setSidebarOpen(false),
            true,
            () => setSidebarOpen(false),
          )}
        </View>
      </Animated.View>

      <View style={styles.mobileHeader} role="banner">
        {header}
      </View>

      {mainAreaContent}

      {aside && (
        <View style={styles.mobileAside} role="complementary">
          <ScrollView showsVerticalScrollIndicator={false}>{aside}</ScrollView>
        </View>
      )}

      {!mobileTabBarHidden && <MobileTabBar />}
    </View>
  );
}

export function DashboardLayout({
  sidebar,
  header,
  children,
  aside,
  scrollable = true,
}: DashboardLayoutProps) {
  const { t } = useTranslation();
  const { isDesktop } = useResponsive();
  // Tablet now collapses into the same drawer-hamburger layout as mobile so
  // tablet portrait widths (780–1079px) don't waste a sidebar gutter and shift
  // page content off-center. The full desktop sidebar only appears at ≥1080px.
  const showDesktopLayout = isDesktop;

  const [expanded, setExpanded] = useState(
    () => Platform.OS === 'web' || Platform.OS === 'macos' || Platform.OS === 'windows',
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [storageLoaded, setStorageLoaded] = useState(false);

  const sidebarWidthAnim = useRef(
    new Animated.Value(expanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED),
  ).current;
  const mobileMenuAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const loadSidebarState = async () => {
      try {
        const stored = await secureStorage.getItem(SIDEBAR_EXPANDED_KEY);
        if (stored !== null) {
          const isExpanded = stored === 'true';
          setExpanded(isExpanded);
          sidebarWidthAnim.setValue(isExpanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED);
        }
      } catch {
        // Best effort only.
      } finally {
        setStorageLoaded(true);
      }
    };

    loadSidebarState();
  }, [sidebarWidthAnim]);

  useEffect(() => {
    if (!storageLoaded) return;

    Animated.spring(sidebarWidthAnim, {
      toValue: expanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED,
      damping: 22,
      mass: 0.9,
      stiffness: 240,
      useNativeDriver: false,
    }).start();
  }, [expanded, sidebarWidthAnim, storageLoaded]);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    Animated.spring(mobileMenuAnim, {
      toValue: sidebarOpen ? 1 : 0,
      damping: 22,
      mass: 0.9,
      stiffness: 260,
      useNativeDriver: true,
    }).start();
  }, [mobileMenuAnim, sidebarOpen]);

  const handleToggle = useCallback(() => {
    const next = !expanded;
    setExpanded(next);
    void secureStorage.setItem(SIDEBAR_EXPANDED_KEY, String(next)).catch(() => {});
  }, [expanded]);

  const getSidebarWithProps = useCallback(
    (toggleHandler: () => void, isExpanded: boolean, onNavigate?: () => void) => {
      if (!isValidElement(sidebar)) {
        return sidebar;
      }
      return cloneElement(sidebar as ReactElement<SidebarLayoutProps>, {
        expanded: isExpanded,
        onToggle: toggleHandler,
        onNavigate,
      });
    },
    [sidebar],
  );

  const sidebarWithProps = useMemo(
    () => getSidebarWithProps(handleToggle, expanded),
    [getSidebarWithProps, handleToggle, expanded],
  );

  if (showDesktopLayout) {
    return (
      <DesktopLayout
        sidebarWithProps={sidebarWithProps}
        header={header}
        mainContent={children}
        aside={aside}
        sidebarWidthAnim={sidebarWidthAnim}
        scrollable={scrollable}
      />
    );
  }

  if (Platform.OS !== 'web') {
    return (
      <NativeMobileLayout
        header={header}
        mainContent={children}
        aside={aside}
        getSidebarWithProps={getSidebarWithProps}
        t={t}
        scrollable={scrollable}
      />
    );
  }

  const mobileDrawerTranslateX = mobileMenuAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [-SIDEBAR_WIDTH_EXPANDED - 24, 0],
  });
  const mobileBackdropOpacity = mobileMenuAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1],
  });

  return (
    <WebMobileLayout
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      header={header}
      mainContent={children}
      aside={aside}
      getSidebarWithProps={getSidebarWithProps}
      mobileMenuAnim={mobileMenuAnim}
      mobileDrawerTranslateX={mobileDrawerTranslateX}
      mobileBackdropOpacity={mobileBackdropOpacity}
      t={t}
      scrollable={scrollable}
    />
  );
}

const styles = StyleSheet.create({
  desktopRoot: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: colors.background.primary,
    overflow: 'hidden',
    // On web, the parent chain (#root has min-height:100dvh but no fixed
    // height) lets flex:1 grow with content instead of being clamped to the
    // viewport. Pinning the layout root to 100dvh anchors the bottom nav and
    // any other flex-bottom child to the actual viewport edge.
    ...(Platform.OS === 'web' ? ({ height: '100dvh' } as Record<string, string>) : {}),
  },
  // No border and no fill of its own: the sidebar it wraps paints the app
  // background, and the container used to stack a second 1px right border on
  // top of SidebarModern's own — a 2px composite seam nobody asked for.
  desktopSidebarContainer: {
    overflow: 'hidden' as const,
  },
  desktopMainArea: {
    flex: 1,
    flexDirection: 'column',
    minWidth: 0,
  },
  desktopHeader: {
    zIndex: 100,
    overflow: 'visible' as any,
  },
  // Chrome and content share one tone, so this is breathing room, not a seam:
  // it keeps content off the sidebar's nav items and the header's controls.
  // There is nothing to pad on the right or bottom — no chrome sits there, and
  // padding those edges would fence the content in on all four sides.
  desktopContentRow: {
    flex: 1,
    flexDirection: 'row',
    minWidth: 0,
    paddingLeft: tokens.spacing.lg,
    paddingTop: tokens.spacing.lg,
  },
  // No fill and no radius: a rounded panel on the same tone as everything
  // around it draws nothing. `overflow: hidden` stays — it clips wide content
  // (charts, kanban) to the column instead of letting it spill past the shell.
  desktopPanel: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  scrollFlex: {
    flex: 1,
    minWidth: 0,
    width: '100%',
    // Lift main content into its own positioned stacking context with a
    // z-index above the absolutely-positioned Atmosphere orbs (z-index: 0
    // by default). Otherwise the cyan/purple glow paints on top of the
    // toolbar and obscures interactive controls.
    position: 'relative',
    zIndex: 1,
  },
  // Measured from the panel's own edges, not the window's, so it stays even
  // on the right where the panel runs to the viewport edge with no shell gap.
  desktopContentContainer: {
    maxWidth: MAX_CONTENT_WIDTH,
    minWidth: 0,
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: tokens.spacing.xl,
    paddingVertical: tokens.spacing.xl,
  },
  desktopContentPadding: {
    minWidth: 0,
    width: '100%',
    paddingHorizontal: tokens.spacing.xl,
    paddingVertical: tokens.spacing.xl,
  },
  // A sibling column separated by spacing rather than a bordered rail. No
  // screen passes an `aside` today; if one does and needs to read as its own
  // region, give it a surface role from the theme rather than a border.
  desktopAside: {
    width: 380,
    marginLeft: tokens.spacing.lg,
    overflow: 'hidden',
  },
  asideScroll: {
    flex: 1,
  },
  asideScrollContent: {
    padding: tokens.spacing.xl,
  },
  mobileRoot: {
    flex: 1,
    backgroundColor: colors.background.primary,
    overflow: 'hidden',
    // See desktopRoot note: a fixed viewport height is required on web so the
    // bottom tab bar (a flex child at the end of this column) sits exactly at
    // the viewport edge instead of being pushed below the fold.
    ...(Platform.OS === 'web' ? ({ height: '100dvh' } as Record<string, string>) : {}),
  },
  drawerContent: {
    flex: 1,
  },
  mobileMenuButton: {
    position: 'absolute',
    left: 14,
    top: (MOBILE_HEADER_HEIGHT - MOBILE_MENU_BUTTON_SIZE) / 2,
    zIndex: tokens.zIndex.modal,
    width: MOBILE_MENU_BUTTON_SIZE,
    height: MOBILE_MENU_BUTTON_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.surface.navChrome,
  },
  mobileBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: tokens.zIndex.sticky,
    backgroundColor: colors.background.backdropTinted,
  },
  mobileSidebar: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    zIndex: tokens.zIndex.modal,
    width: SIDEBAR_WIDTH_EXPANDED,
  },
  // Notification panel slides in from the right edge of mobileRoot. Width
  // is explicit (not flexed) so transform-based animations stay smooth
  // without remeasuring on every frame.
  notificationDrawer: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    zIndex: tokens.zIndex.modal,
    width: NOTIFICATION_DRAWER_WIDTH,
    backgroundColor: colors.background.secondary,
    borderLeftWidth: 1,
    borderLeftColor: colors.border.subtle,
  },
  mobileSidebarInner: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  mobileHeader: {
    // Background is owned by DashboardHeader, which paints the app background
    // on every platform. Mobile content runs edge to edge under a header on
    // that same tone, so there is no seam here at all — the drawer scrim and
    // the header's own spacing carry the separation.
    // Atmosphere orbs are absolutely positioned siblings and would otherwise
    // paint on top of the header (positioned elements paint after static
    // siblings in CSS). Forcing the header into its own positioned stacking
    // context with a higher z-index keeps the burger + trailing icons visible
    // above the cyan glow.
    position: 'relative',
    zIndex: 2,
  },
  mobileContentContainer: {
    // Native (iOS/Android) gets a wider gutter so the list visually breathes
    // away from the screen edges. Web keeps the tighter 16px gutter so the
    // already-narrow tablet/mobile-web viewport doesn't waste horizontal
    // space.
    paddingHorizontal: Platform.OS === 'web' ? tokens.spacing.lg : tokens.spacing.xl,
    paddingTop: tokens.spacing.xl,
    paddingBottom: tokens.spacing['2xl'],
  },
  mobileContentPadding: {
    paddingHorizontal: Platform.OS === 'web' ? tokens.spacing.lg : tokens.spacing.xl,
  },
  mobileAside: {
    borderTopWidth: 1,
    borderTopColor: colors.border.subtle,
    backgroundColor: colors.surface.cardOverlay,
    padding: tokens.spacing.lg,
    maxHeight: '42%',
  },
  orbTop: {
    position: 'absolute',
    top: -110,
    right: -80,
    width: 360,
    height: 360,
    borderRadius: 360,
    backgroundColor: colors.glow.cyan,
  },
  orbBottom: {
    position: 'absolute',
    bottom: -180,
    left: -120,
    width: 420,
    height: 420,
    borderRadius: 420,
    backgroundColor: colors.glow.purple,
  },
});
